import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import GroupStageView from './GroupStageView'; // old GSL / mini-elim group stages only
import EliminationView from './bracket/EliminationView';
import SwissView from './bracket/SwissView';
import MajorStageLayout from './bracket/MajorStageLayout';
import GroupsView from './bracket/GroupsView';
import { placeholderTeams } from './bracket/bracketText';
import './bracket/bracket.css';
import PublicHeader from './PublicHeader';
import Footer from './Footer';
import './PublicMatches.css';
import usePageTitle from '../utils/usePageTitle';
import { useTranslation } from 'react-i18next';

// Public API client — never sends auth token so server always applies public sanitization
const publicApi = axios.create();
delete publicApi.defaults.headers.common['Authorization'];

function PublicBracket() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [bracket, setBracket] = useState(null);
  usePageTitle(bracket ? bracket.name : null);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [groups, setGroups] = useState([]);
  const [swissStandings, setSwissStandings] = useState([]);
  const [playoffs, setPlayoffs] = useState(null); // two-stage bracket: the stage's playoffs
  const [tab, setTab] = useState(null);
  const [seasonName, setSeasonName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchBracket = useCallback(async (silent = false) => {
    try {
      const res = await publicApi.get(`/api/brackets/${id}/public`);
      if (mountedRef.current) {
        setBracket(res.data.bracket);
        setMatches(res.data.matches || []);
        setTeams(res.data.teams || {});
        setGroups(res.data.groups || []);
        setSwissStandings(res.data.swiss_standings || []);
        setPlayoffs(res.data.playoffs || null);
        setSeasonName(res.data.season_name || '');
        if (!silent) setLoading(false);
      }
    } catch (err) {
      if (!silent && mountedRef.current) {
        setError(err.response?.data?.error || 'Failed to load bracket');
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchBracket();
  }, [fetchBracket]);

  // Public WebSocket for live updates
  useEffect(() => {
    mountedRef.current = true;

    function connectWs() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const wsUrl = `${protocol}//${host}${port}/ws/public`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'bracket_update' || message.type === 'score_update' || message.type === 'match_finished' || message.type === 'match_status_change') {
              fetchBracket(true);
            }
          } catch (_) {}
        };

        ws.onclose = () => {
          if (mountedRef.current) {
            reconnectRef.current = setTimeout(connectWs, 3000);
          }
        };

        ws.onerror = () => {
          try { ws.close(); } catch (_) {}
        };
      } catch (_) {}
    }

    connectWs();

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
      }
    };
  }, [fetchBracket]);

  // two-stage bracket: open on the playoffs once they have started
  const majorSwiss = !!(bracket && bracket.bracket_type === 'swiss' && bracket.swiss_info && bracket.swiss_info.mode === 'major');
  const activeTab = !playoffs ? 'stage' : (tab || (!playoffs.preview && playoffs.bracket && playoffs.bracket.status !== 'draft' ? 'playoffs' : 'stage'));

  if (loading) {
    return (
      <div className="pub-root">
        <PublicHeader />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
          <div className="pub-spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !bracket) {
    return (
      <div className="pub-root">
        <PublicHeader />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem 1rem', color: 'var(--accent-red)' }}>
          {error || 'Bracket not found'}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pub-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <PublicHeader />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0.5rem 0.75rem', overflow: 'hidden' }}>
        {/* Header - compact */}
        <div style={{
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <Link to="/" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.78rem', transition: 'color 0.12s' }}>
            ← Back
          </Link>
          <span style={{ color: 'var(--border-default, rgba(255,255,255,0.09))' }}>|</span>
          {seasonName && (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
              {seasonName}
            </span>
          )}
          <h1 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            {bracket.name}
          </h1>
          <span style={{
            background: bracket.status === 'completed' ? 'rgba(34,197,94,0.08)' : bracket.status === 'finished' ? 'rgba(139,92,246,0.08)' : bracket.status === 'cancelled' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
            border: `1px solid ${bracket.status === 'completed' ? 'rgba(34,197,94,0.2)' : bracket.status === 'finished' ? 'rgba(139,92,246,0.2)' : bracket.status === 'cancelled' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}`,
            color: bracket.status === 'completed' ? 'var(--accent-green)' : bracket.status === 'finished' ? '#a78bfa' : bracket.status === 'cancelled' ? 'var(--accent-red)' : 'var(--accent-amber)',
            padding: '2px 7px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
          }}>
            {t(`bracketStatus.${bracket.status}`, bracket.status)}
          </span>
          <span style={{
            background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
            color: 'var(--accent-blue)', padding: '2px 7px', borderRadius: 'var(--radius-sm, 6px)', fontSize: '0.72rem', fontWeight: 600,
          }}>
            {t('bracket.teamsN', '{{n}} teams', { n: bracket.team_count })}
          </span>
        </div>

        {playoffs && !majorSwiss && (
          <div className="br-stages" style={{ marginBottom: '0.5rem' }}>
            <button type="button" className={activeTab === 'stage' ? 'on' : ''} onClick={() => setTab('stage')}>
              <span className="no">1</span>{bracket.bracket_type === 'swiss' ? t('bracket.type.swiss', 'Swiss') : t('bracket.type.groups', 'Group stage')}
            </button>
            <span className="arrow">→</span>
            <button type="button" className={activeTab === 'playoffs' ? 'on' : ''} onClick={() => setTab('playoffs')}>
              <span className="no">2</span>{t('bracket.po.title', 'Playoffs')}
            </button>
          </div>
        )}

        {/* Bracket View - takes all remaining space */}
        <div style={{
          flex: 1,
          borderRadius: 'var(--radius-md, 8px)',
          border: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
          background: 'var(--bg-card, #111118)',
          overflow: 'hidden',
          minHeight: 0,
        }}>
          {majorSwiss ? (
            // Major-style Swiss: playoffs on top, group play graph below (like HLTV's event page)
            <div className="br-page" style={{ padding: '0.8rem', overflow: 'auto', height: '100%', boxSizing: 'border-box' }}>
              <MajorStageLayout
                playoffs={playoffs ? (<>
                  {playoffs.preview && <div className="br-hint" style={{ marginBottom: '0.5rem' }}>{t('bracket.po.publicPreview', 'The playoffs fill in from the standings when the stage ends.')}</div>}
                  <EliminationView bracket={playoffs.bracket} matches={playoffs.matches} teams={placeholderTeams(t, playoffs.teams)} readOnly linkPrefix="/match" />
                </>) : null}
                bracket={bracket} matches={matches} teams={teams} standings={swissStandings} readOnly linkPrefix="/match" />
            </div>
          ) : activeTab === 'playoffs' ? (
            <div style={{ padding: '0.8rem', overflow: 'auto', height: '100%', boxSizing: 'border-box' }}>
              {playoffs.preview && <div className="br-hint" style={{ marginBottom: '0.5rem' }}>{t('bracket.po.publicPreview', 'The playoffs fill in from the standings when the stage ends.')}</div>}
              <EliminationView bracket={playoffs.bracket} matches={playoffs.matches} teams={placeholderTeams(t, playoffs.teams)} readOnly linkPrefix="/match" />
            </div>
          ) : bracket.bracket_type === 'swiss' ? (
            <div style={{ padding: '0.8rem', overflow: 'auto', height: '100%', boxSizing: 'border-box' }}>
              <SwissView bracket={bracket} matches={matches} teams={teams} standings={swissStandings} readOnly linkPrefix="/match" />
            </div>
          ) : bracket.bracket_type === 'group_stage' && (groups || []).length > 0 && groups.every(g => g.group_format === 'round_robin' || g.group_format === 'dual') ? (
            <div style={{ padding: '0.8rem', overflow: 'auto', height: '100%', boxSizing: 'border-box' }}>
              <GroupsView bracket={bracket} matches={matches} teams={teams} groups={groups} readOnly linkPrefix="/match" />
            </div>
          ) : bracket.bracket_type === 'group_stage' ? (
            // old GSL / mini-elim group stages
            <GroupStageView
              bracketData={{ matches, teams, groups }}
              bracketStatus={bracket.status}
              readOnly={true}
              matchLinkPrefix="/match"
              onSetWinner={() => {}}
              onResetMatch={() => {}}
              onScheduleMatch={() => {}}
              servers={[]}
            />
          ) : (
            <EliminationView bracket={bracket} matches={matches} teams={teams} readOnly linkPrefix="/match" />
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicBracket;
